import axios, { type AxiosRequestConfig, type AxiosError } from 'axios';
import { ApiError } from './api-errors';

// ✅ Industry Standard Token Keys
export const AUTH_TOKEN_KEY = 'semo_access_token';
export const REFRESH_TOKEN_KEY = 'semo_refresh_token';

// ============================================================================
// Internal Storage Helpers (Safe for SSR)
// ============================================================================

/**
 * 로그인 정보를 어디에 담아둘지 고르는 스위치.
 *
 * - 'local'   → 브라우저를 닫았다 열어도 로그인이 남아 있다 (발주기관·공급사 화면)
 * - 'session' → 탭을 닫으면 로그인이 풀린다 (마스터 화면)
 *
 * 기본값은 'local' 이고, 세션 방식을 쓰려는 앱이 부팅할 때
 * `configureAuthStorage('session')` 을 한 번 불러 바꾼다.
 */
export type AuthStorageMode = 'local' | 'session';

let authStorageMode: AuthStorageMode = 'local';

export const configureAuthStorage = (mode: AuthStorageMode) => {
  authStorageMode = mode;
};

export const getAuthStorage = (): Storage | null => {
  if (typeof window === 'undefined') return null;
  return authStorageMode === 'session' ? window.sessionStorage : window.localStorage;
};

/**
 * "로그인 유지" 체크박스가 실제로 하는 일.
 *
 * - 체크함(true)  → 브라우저를 닫았다 열어도 로그인이 남는 저장소
 * - 체크 안 함     → 탭을 닫으면 로그인이 사라지는 저장소
 *
 * 반대쪽 저장소에 남아 있던 로그인 흔적은 여기서 같이 걷어낸다.
 * (안 그러면 "유지 안 함" 으로 로그인해도 예전에 남은 게 되살아난다.)
 */
export const setAuthPersistence = (remember: boolean) => {
  const next: AuthStorageMode = remember ? 'local' : 'session';

  if (typeof window !== 'undefined') {
    const other = next === 'local' ? window.sessionStorage : window.localStorage;
    other.removeItem(AUTH_TOKEN_KEY);
    other.removeItem(REFRESH_TOKEN_KEY);
  }

  configureAuthStorage(next);
};

/**
 * 새로고침·재방문 때 로그인이 어느 저장소에 담겨 있는지 찾아 그쪽을 쓰도록 맞춘다.
 * 탭 저장소에 들어 있으면 이번 로그인은 "유지 안 함" 으로 시작한 것이다.
 * 앱이 부팅할 때 `setupApiClient` 보다 먼저 한 번 부른다.
 */
export const restoreAuthPersistence = (): AuthStorageMode => {
  if (typeof window === 'undefined') return 'local';

  const mode: AuthStorageMode = window.sessionStorage.getItem(AUTH_TOKEN_KEY)
    ? 'session'
    : 'local';

  configureAuthStorage(mode);
  return mode;
};

/**
 * 로그아웃할 때는 두 저장소를 모두 비운다.
 * 어느 쪽으로 로그인했든 흔적이 남지 않게 한다.
 */
export const clearStoredAuthTokens = () => {
  if (typeof window === 'undefined') return;

  for (const storage of [window.localStorage, window.sessionStorage]) {
    storage.removeItem(AUTH_TOKEN_KEY);
    storage.removeItem(REFRESH_TOKEN_KEY);
  }
};

const getStorageItem = (key: string) => getAuthStorage()?.getItem(key) ?? null;

const setStorageItem = (key: string, value: string) => {
  getAuthStorage()?.setItem(key, value);
};

const removeStorageItem = (key: string) => {
  getAuthStorage()?.removeItem(key);
};

// ============================================================================
// API Client Instance
// ============================================================================

const defaultConfig: AxiosRequestConfig = {
  baseURL: '/',
  withCredentials: true,
  timeout: 30000,
  headers: {},
  paramsSerializer: {
    indexes: null, // ✅ Array serialization: ?a=1&a=2 (required for NestJS)
  },
};

export const instance = axios.create(defaultConfig);

/**
 * Extended config for internal retry state
 */
interface CustomAxiosRequestConfig extends AxiosRequestConfig {
  _retry?: boolean; // For Auth 401 Retry
  _retryCount?: number; // For 5xx/Network Retry
}

/**
 * API 클라이언트 설정 주입
 */
export const setupApiClient = (config: AxiosRequestConfig & { apiKey?: string; token?: string }) => {
  if (config.baseURL) instance.defaults.baseURL = config.baseURL;
  if (config.headers) Object.assign(instance.defaults.headers.common, config.headers);
  if (config.apiKey) instance.defaults.headers.common['X-API-Key'] = config.apiKey;
  if (config.timeout) instance.defaults.timeout = config.timeout;
  if (config.withCredentials !== undefined) instance.defaults.withCredentials = config.withCredentials;

  // ✅ Auto-load token from storage if not explicitly provided
  const token = config.token || getStorageItem(AUTH_TOKEN_KEY);
  if (token) setAuthToken(token);
};

export const setAuthToken = (token: string) => {
  instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
};

export const clearAuthToken = () => {
  delete instance.defaults.headers.common['Authorization'];
};

/**
 * @deprecated Use `setupApiClient` instead for full configuration.
 * Retained for backward compatibility during refactor.
 */
export const setApiClientHeaders = (newHeaders: Record<string, string>) => {
  Object.assign(instance.defaults.headers.common, newHeaders);
};

type ApiConfig = AxiosRequestConfig & { url: string; method: string };

/**
 * API 호출 함수
 */
export const api = async <T>(config: ApiConfig): Promise<T> => {
  try {
    const res = await instance(config);
    return res.data as T;
  } catch (error) {
    throw ApiError.fromUnknown(error);
  }
};

// ============================================================================
// 🔄 Retry Logic (Industry Standard: Exponential Backoff)
// ============================================================================

const MAX_RETRIES = 0; // Disable automatic retries to prevent multiple UI toasts
const INITIAL_DELAY_MS = 1000;
const BACKOFF_FACTOR = 2;

const wait = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

const isRetryableError = (error: any): boolean => {
  if (!error.response) {
    // Network Error (No response)
    return true;
  }
  const status = error.response.status;
  // Retry on Server Errors (5xx) or Too Many Requests (429)
  return (status >= 500 && status <= 599) || status === 429;
};

// ============================================================================
// 🔒 Auth Refresh Logic & Error Handling
// ============================================================================

let isRefreshing = false;
type RefreshCallback = (error?: any) => void;
let refreshSubscribers: RefreshCallback[] = [];

// Auth Error Callbacks (e.g., for triggering logout in UI)
type AuthErrorCallback = () => void;
let authErrorSubscribers: AuthErrorCallback[] = [];

export const registerAuthErrorCallback = (callback: AuthErrorCallback) => {
  authErrorSubscribers.push(callback);
  // Return unsubscribe function
  return () => {
    authErrorSubscribers = authErrorSubscribers.filter((cb) => cb !== callback);
  };
};

const notifyAuthError = () => {
  authErrorSubscribers.forEach((cb) => cb());
};

const onRefreshed = () => {
  refreshSubscribers.forEach((cb) => cb());
  refreshSubscribers = [];
};

const onRefreshFailed = (error: any) => {
  refreshSubscribers.forEach((cb) => cb(error));
  refreshSubscribers = [];
};

const addRefreshSubscriber = (callback: RefreshCallback) => {
  refreshSubscribers.push(callback);
};

instance.interceptors.response.use(
  (response) => response,
  async (error: AxiosError) => {
    const originalRequest = error.config as CustomAxiosRequestConfig;
    if (!originalRequest) return Promise.reject(error);

    // ------------------------------------------------------------------------
    // 1. Handle 401 Unauthorized (Token Refresh)
    // ------------------------------------------------------------------------
    const isRefreshRequest = originalRequest.url?.includes('auth/refresh');
    const isLogoutRequest = originalRequest.url?.includes('auth/logout');
    const isLoginRequest = originalRequest.url?.includes('auth/login');

    if (
      error.response?.status === 401 &&
      !originalRequest._retry &&
      !isRefreshRequest &&
      !isLogoutRequest &&
      !isLoginRequest
    ) {
      // ✅ Skip Auth Refresh for Embed Pages (Stateless / Token in URL)
      // Embed pages manage their own auth state via EmbedAuthProvider and should not trigger global logout/redirects
      if (typeof window !== 'undefined' && window.location.pathname.startsWith('/embed')) {
        return Promise.reject(error);
      }

      // 로그인하지 않은 화면(저장된 로그인이 없음)의 401 은 «세션 만료» 가 아니다 — 재발급을 시도하지 않고
      // 서버가 보낸 사유를 그대로 돌려준다. 예: 비밀번호 재설정 15분이 지나 [변경 완료] →
      // «유효하지 않은 재설정 토큰입니다»(매뉴얼 R06 2.3.4.3 7번). 예전엔 재발급 실패로 바뀌어
      // 화면에 «오류가 발생했습니다.» 만 떴다.
      if (!getStorageItem(REFRESH_TOKEN_KEY) && !getStorageItem(AUTH_TOKEN_KEY)) {
        return Promise.reject(error);
      }

      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          originalRequest._retry = true;
          addRefreshSubscriber((err?: any) => {
            if (err) return reject(err);
            resolve(instance(originalRequest));
          });
        });
      }

      isRefreshing = true;
      originalRequest._retry = true;

      try {
        // ✅ Bearer Token Refresh Pattern
        const refreshToken = getStorageItem(REFRESH_TOKEN_KEY);
        if (!refreshToken) {
          throw new Error('No refresh token available');
        }

        // 재발급 요청도 나머지 요청과 똑같이 baseURL 을 그대로 탄다.
        // 여기에 '/api/v1' 을 직접 붙여두는 동안, 그 접두사가 이미 들어 있는 환경에서는
        // 주소가 두 번 겹쳐 404 가 났고 → 앱은 그걸 '로그인 만료' 로 읽고 강제 로그아웃했다.
        const response = await instance.post('/auth/refresh', { refreshToken });
        const { accessToken: newAccessToken, refreshToken: newRefreshToken } = response.data.data;

        if (newAccessToken) {
          setStorageItem(AUTH_TOKEN_KEY, newAccessToken);
          setAuthToken(newAccessToken);
          
          // ✅ Explicitly set header for the original request retry
          if (originalRequest.headers) {
            originalRequest.headers['Authorization'] = `Bearer ${newAccessToken}`;
          }

          if (newRefreshToken) {
            setStorageItem(REFRESH_TOKEN_KEY, newRefreshToken);
          }
        }

        isRefreshing = false;
        onRefreshed();
        return instance(originalRequest);
      } catch (refreshError) {
        isRefreshing = false;
        onRefreshFailed(refreshError);

        // Clear tokens on persistent refresh failure
        removeStorageItem(AUTH_TOKEN_KEY);
        removeStorageItem(REFRESH_TOKEN_KEY);
        clearAuthToken();

        // 🚨 Notify UI to Force Logout
        notifyAuthError();

        return Promise.reject(refreshError);
      }
    }

    // ------------------------------------------------------------------------
    // 2. Handle Retryable Errors (5xx, Network)
    // ------------------------------------------------------------------------
    if (isRetryableError(error)) {
      originalRequest._retryCount = originalRequest._retryCount || 0;

      if (originalRequest._retryCount < MAX_RETRIES) {
        originalRequest._retryCount++;

        const delay = INITIAL_DELAY_MS * Math.pow(BACKOFF_FACTOR, originalRequest._retryCount - 1);
        const jitter = delay * 0.1 * Math.random();
        const finalDelay = delay + jitter;

        await wait(finalDelay);
        return instance(originalRequest);
      }
    }

    return Promise.reject(error);
  }
);